/**
 * Benefícios do funcionário — helpers PUROS do histórico por benefício
 * (rodada 03/09/2026, pedido do dono): a "visão geral" lista TODOS os
 * benefícios que a pessoa já teve (vigentes, futuros e encerrados juntos) e o
 * modal de cada benefício mostra a linha do tempo das vigências com "o que
 * mudou aqui" (diff de cada vigência × a anterior da cadeia — ex.: "a partir
 * de janeiro 100, a partir de março 200, abril cancela"). Tudo derivado da
 * cadeia já carregada pela aba — nenhum fetch novo. Tipos e funções de
 * exibição movidos de paginas/funcionario/BeneficiosTab.tsx nesta rodada
 * (sem mudança de comportamento).
 */
import { fmtMoeda } from "./format";

export interface Vinculo {
  id: string;
  beneficioTipoId: string;
  tipoNome: string;
  tipoPapel: string;
  tipoModeloCalculo: string;
  valorCentavos: number | null;
  descontoValorCentavos?: number | null;
  vigenciaInicio: number;
  vigenciaFim: number | null;
  ativo: boolean;
  observacao: string | null;
  tarifas: { ordem: number; tarifaCentavos: number; quantidade: number; tarifaCatalogoId?: string | null; tarifaNome?: string | null }[];
  beneficiarios: { descricao: string; mensalidadeCentavos: number; percentualFuncionario: number; percentualEmpresa: number }[];
}

/** Um benefício (tipo) na pessoa = a CADEIA de vigências no tempo. */
export interface GrupoBeneficio {
  tipoId: string;
  tipoNome: string;
  /** Vigências em ordem cronológica (início crescente). */
  cadeia: Vinculo[];
  /** A vigência em DESTAQUE na data-base: a que cobre o mês; senão a futura mais
   *  próxima; senão a mais recente (encerrada). */
  principal: Vinculo;
  /** Tudo encerrado antes da data-base (vai para a seção "Encerrados"). */
  encerrado: boolean;
}

export function statusVinculo(v: Vinculo, comp: number): { rotulo: string; cor: string } {
  if (v.vigenciaFim !== null && comp > v.vigenciaFim) return { rotulo: "encerrado", cor: "gray" };
  if (comp < v.vigenciaInicio) return { rotulo: "futuro", cor: "blue" };
  return { rotulo: "vigente", cor: "sillis" };
}

export function detalheVinculo(v: Vinculo): string {
  if (v.tipoModeloCalculo === "cap_salario_custo" && v.tarifas.length > 0) {
    return v.tarifas.map((t) => `${t.tarifaNome ? `${t.tarifaNome} ` : ""}${fmtMoeda(t.tarifaCentavos)}×${t.quantidade}/dia`).join(" + ");
  }
  if (v.tipoModeloCalculo === "plano_saude_rateio" && v.beneficiarios.length > 0) {
    return v.beneficiarios.map((b) => `${b.descricao} ${fmtMoeda(b.mensalidadeCentavos)} (${b.percentualFuncionario}%/${b.percentualEmpresa}%)`).join(" · ");
  }
  if (v.valorCentavos !== null) return `valor do vínculo: ${fmtMoeda(v.valorCentavos)} (override)`;
  return "valor do parâmetro global vigente no mês";
}

/** Sufixo do detalhe com o override do desconto (Tarefa A), quando existe. */
export function detalheDesconto(v: Vinculo): string {
  return v.descontoValorCentavos != null ? ` · desconto override: ${fmtMoeda(v.descontoValorCentavos)}` : "";
}

export function agruparPorBeneficio(vinculos: Vinculo[], compRef: number): GrupoBeneficio[] {
  const porTipo = new Map<string, Vinculo[]>();
  for (const v of vinculos) {
    const lista = porTipo.get(v.beneficioTipoId) ?? [];
    lista.push(v);
    porTipo.set(v.beneficioTipoId, lista);
  }
  return [...porTipo.entries()]
    .map(([tipoId, lista]) => {
      const cadeia = [...lista].sort((a, b) => a.vigenciaInicio - b.vigenciaInicio);
      const cobre = cadeia.find((v) => v.vigenciaInicio <= compRef && (v.vigenciaFim === null || v.vigenciaFim >= compRef));
      const futura = cadeia.find((v) => v.vigenciaInicio > compRef);
      const principal = cobre ?? futura ?? cadeia[cadeia.length - 1]!;
      const encerrado = cadeia.every((v) => v.vigenciaFim !== null && v.vigenciaFim < compRef);
      return { tipoId, tipoNome: principal.tipoNome, cadeia, principal, encerrado };
    })
    .sort((a, b) => a.tipoNome.localeCompare(b.tipoNome, "pt-BR"));
}

// ---------------------------------------------------------------------------
// Rodada 03/09/2026 — diff "o que mudou aqui" e período total da cadeia
// ---------------------------------------------------------------------------

/** Rótulo de um valor do vínculo (null = o vínculo manda no parâmetro global). */
function rotuloValor(v: number | null | undefined): string {
  return v == null ? "parâmetro global" : `R$ ${fmtMoeda(v)}`;
}

/** Rótulo do override de desconto (null = sem override — vale o tipo/parâmetro). */
function rotuloDesconto(v: number | null | undefined): string {
  return v == null ? "sem override" : `R$ ${fmtMoeda(v)}`;
}

function rotuloObs(o: string | null): string {
  return o === null || o === "" ? "(vazia)" : `"${o}"`;
}

/** Assinatura comparável das tarifas (ordem não conta — `ordem` é posicional). */
function assinaturaTarifas(v: Vinculo): string {
  return JSON.stringify(v.tarifas.map((t) => [t.tarifaCentavos, t.quantidade]).sort((a, b) => a[0]! - b[0]!));
}

function assinaturaBeneficiarios(v: Vinculo): string {
  return JSON.stringify(
    v.beneficiarios.map((b) => [b.descricao, b.mensalidadeCentavos, b.percentualFuncionario, b.percentualEmpresa]).sort(),
  );
}

/** O que mudou NAQUELA vigência vs a anterior da cadeia (só o que mudou;
 *  anterior null = a criação; nada mudou = uma linha honesta). */
export function diffVigencia(atual: Vinculo, anterior: Vinculo | null): string[] {
  if (anterior === null) return ["criação do vínculo"];
  const mudancas: string[] = [];
  if ((atual.valorCentavos ?? null) !== (anterior.valorCentavos ?? null)) {
    mudancas.push(`valor: ${rotuloValor(anterior.valorCentavos)} → ${rotuloValor(atual.valorCentavos)}`);
  }
  if ((atual.descontoValorCentavos ?? null) !== (anterior.descontoValorCentavos ?? null)) {
    mudancas.push(`desconto: ${rotuloDesconto(anterior.descontoValorCentavos)} → ${rotuloDesconto(atual.descontoValorCentavos)}`);
  }
  if ((atual.observacao ?? null) !== (anterior.observacao ?? null)) {
    mudancas.push(`observação: ${rotuloObs(anterior.observacao)} → ${rotuloObs(atual.observacao)}`);
  }
  if (assinaturaTarifas(atual) !== assinaturaTarifas(anterior)) {
    mudancas.push(
      atual.tarifas.length !== anterior.tarifas.length
        ? `tarifas: ${anterior.tarifas.length} → ${atual.tarifas.length}`
        : "tarifas alteradas (mesmo nº)",
    );
  }
  if (assinaturaBeneficiarios(atual) !== assinaturaBeneficiarios(anterior)) {
    mudancas.push(
      atual.beneficiarios.length !== anterior.beneficiarios.length
        ? `beneficiários: ${anterior.beneficiarios.length} → ${atual.beneficiarios.length}`
        : "beneficiários alterados (mesmo nº)",
    );
  }
  return mudancas.length > 0 ? mudancas : ["sem mudança em relação à vigência anterior"];
}

/** Menor vigenciaInicio → maior vigenciaFim (null se alguma segue em vigor). */
export function periodoTotalDaCadeia(cadeia: Vinculo[]): { inicio: number; fim: number | null } {
  if (cadeia.length === 0) return { inicio: 0, fim: null };
  const inicio = Math.min(...cadeia.map((v) => v.vigenciaInicio));
  const fim = cadeia.some((v) => v.vigenciaFim === null) ? null : Math.max(...cadeia.map((v) => v.vigenciaFim!));
  return { inicio, fim };
}
